import { useCallback, useEffect } from 'react';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import { useAuth } from '@hooks/useAuth';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import wait from '@helpers/wait';
import { useResponsive } from '@hooks/useResponsive';

export default function useMediaViewerTextHighlightTour({
  isAssetReady,
  isDocument,
  isPreparing,
  isUnsupported,
  setSelectedCollapse
}: {
  isAssetReady: boolean;
  isDocument: boolean;
  isPreparing: boolean;
  isUnsupported: boolean;
  setSelectedCollapse?: (collapse: string | null) => void;
}) {
  const user = useAuth(false);
  const { isLgWidth } = useResponsive();
  const { selectComment, setCanvasMode, setDrawToolsShape } =
    useAssetViewerState();
  const { onStartTour, tourData, onCompleteStep } = useOnboardingProcess();

  const completedOnboardingSteps = user?.user?.completedOnboardingSteps || [];

  const onStartTextHighlight = useCallback(() => {
    onStartTour({
      type: 'text-highlight',
      onClickNext: async (currentStep, isExitTour) => {
        if (isExitTour) {
          onCompleteStep({ type: 'text-highlight', hideCongrats: true });
          return;
        }
        if (currentStep === 0) {
          selectComment('new');
          setCanvasMode('edit');
          setDrawToolsShape('documentHighlight');
        }
        if (currentStep === 1) {
          setSelectedCollapse?.('comments');
          await wait(500);
        }
      },
      onClickPrev: (currentStep, isExitTour) => {
        if (isExitTour) {
          onCompleteStep({ type: 'text-highlight', hideCongrats: true });
          return;
        }
        if (currentStep === 1) {
          setCanvasMode('view');
          return;
        }
        if (currentStep === 2) {
          setCanvasMode('edit');
        }
      }
    });
  }, [
    onStartTour,
    onCompleteStep,
    selectComment,
    setCanvasMode,
    setDrawToolsShape,
    setSelectedCollapse
  ]);

  useEffect(() => {
    if (!isLgWidth) return;
    if (user?.user?.type !== 'internal') return;
    if (tourData?.type) return;
    if (completedOnboardingSteps.includes('text-highlight')) return;
    if (!isDocument || isUnsupported) return;
    if (isPreparing) return;
    if (!isAssetReady) return;
    onStartTextHighlight();
  }, [
    isAssetReady,
    isDocument,
    isPreparing,
    isUnsupported,
    completedOnboardingSteps,
    user?.user?.type,
    tourData?.type,
    onStartTextHighlight,
    isLgWidth
  ]);

  return null;
}
